import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ShareButton from '../../../components/ui/ShareButton';
import useFavorites from '../../../hooks/useFavorites';

const BibleInfo = ({ bible }) => {
  const { toggleFavorite, isProductFavorited, loading } = useFavorites();
  const [isLiked, setIsLiked] = useState(false);

  React.useEffect(() => {
    if (bible?.id) {
      setIsLiked(isProductFavorited(bible.id, 'bible'));
    }
  }, [bible?.id, isProductFavorited]);

  const handleToggleFavorite = async () => {
    if (!bible?.id || loading) return;
    
    const result = await toggleFavorite(bible.id, 'bible');
    if (result.success) {
      setIsLiked(result.isFavorited);
    }
  };

  if (!bible) return null;

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Title */}
      <div>
        <h1 className="text-lg sm:text-2xl lg:text-3xl font-heading font-bold text-foreground leading-tight">
          {bible.title}
        </h1>
      </div>

      {/* Bible Details with Actions */}
      <div className="space-y-3">
        {/* Version/Author */}
        <div className="flex items-center gap-2">
          <Icon name="BookOpen" size={16} className="text-muted-foreground" />
          <span className="text-muted-foreground">Versión:</span>
          <span className="font-medium text-foreground">
            {bible.version || bible.authorName || 'Versión no especificada'}
          </span>
        </div>

        {/* Bible Characteristics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {/* Language */}
          {bible.language && (
            <div className="flex items-center gap-1">
              <Icon name="Globe" size={12} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Idioma:</span>
              <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full text-xs font-medium">
                {bible.language}
              </span>
            </div>
          )}

          {/* Format */}
          {bible.format && (
            <div className="flex items-center gap-1">
              <Icon name="Package" size={12} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Formato:</span>
              <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full text-xs font-medium">
                {bible.format}
              </span>
            </div>
          )}

          {/* Cover Status */}
          <div className="flex items-center gap-1">
            <Icon name="Book" size={12} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Forro:</span>
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
              bible.has_cover 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {bible.has_cover ? 'Con forro' : 'Sin forro'}
            </span>
          </div>

          {/* Tab Index Status */}
          <div className="flex items-center gap-1">
            <Icon name="Bookmark" size={12} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Pestañas:</span>
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
              bible.has_tab_index 
                ? 'bg-orange-100 text-orange-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {bible.has_tab_index ? 'Con pestañas' : 'Sin pestañas'}
            </span>
          </div>
        </div>

        {/* Availability Status */}
        <div className={`p-3 border rounded-lg ${
          bible.available 
            ? 'bg-success/10 border-success/20' 
            : 'bg-destructive/10 border-destructive/20'
        }`}>
          <div className="flex items-center justify-center space-x-2">
            <Icon 
              name={bible.available ? 'CheckCircle' : 'XCircle'} 
              size={18} 
              className={bible.available ? 'text-success' : 'text-destructive'} 
            />
            <span className={`font-medium text-sm ${
              bible.available ? 'text-success' : 'text-destructive'
            }`}>
              {bible.available ? 'Disponible' : 'No disponible'}
            </span>
          </div>
        </div>

        {/* Favorites Button */}
        <Button
          onClick={handleToggleFavorite}
          className="w-full transition-colors duration-200 border"
          style={{
            backgroundColor: !isLiked ? '#d96f00' : '',
            borderColor: !isLiked ? 'transparent' : '',
          }}
          onMouseEnter={(e) => {
            if (!isLiked) {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#d96f00';
              e.currentTarget.style.borderColor = '#d96f00';
              const icon = e.currentTarget.querySelector('.heart-icon');
              if (icon) icon.style.color = '#d96f00';
            }
          }}
          onMouseLeave={(e) => {
            if (!isLiked) {
              e.currentTarget.style.backgroundColor = '#d96f00';
              e.currentTarget.style.color = '#ffffff';
              e.currentTarget.style.borderColor = 'transparent';
              const icon = e.currentTarget.querySelector('.heart-icon');
              if (icon) icon.style.color = '#ffffff';
            }
          }}
        >
          <Icon
            name="Heart"
            size={20}
            className="mr-2 heart-icon"
            style={{
              color: !isLiked ? '#d96f00' : '#d96f00',
              transition: 'color 0.2s ease',
            }}
          />
          {isLiked ? 'Quitar de Favoritos' : 'Agregar a Favoritos'}
        </Button>
      </div>

      {/* Price and Share */}
      <div className="pt-2 border-t border-border">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-2">
            {/* Si tiene ambos (forro + pestañas), mostrar todo en una línea */}
            {bible.has_cover && bible.has_tab_index && bible.price_cover > 0 && bible.price_tab_index > 0 ? (
              <>
                <div className="flex items-baseline gap-4 flex-wrap">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm text-muted-foreground">Precio base:</span>
                    <span className="text-sm font-bold text-primary">
                      ${bible.price?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm text-muted-foreground">Forro:</span>
                    <span className="text-sm font-semibold text-green-600">
                      ${bible.price_cover?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm text-muted-foreground">Pestañas:</span>
                    <span className="text-sm font-semibold text-orange-600">
                      ${bible.price_tab_index?.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="flex items-baseline gap-3 pt-2 border-t border-border/50">
                  <span className="text-lg text-muted-foreground">Total con forro + pestañas:</span>
                  <span className="text-2xl sm:text-4xl font-bold text-blue-600">
                    ${bible.total_with_both?.toFixed(2)}
                  </span>
                </div>
              </>
            ) : (
              <>
                {/* Precio base cuando no tiene ambos - mostrar precios en línea */}
                <div className="flex items-baseline gap-4 flex-wrap">
                  <div className="flex items-baseline gap-2">
                    <span className={`${(bible.has_cover && bible.price_cover > 0) || (bible.has_tab_index && bible.price_tab_index > 0) ? 'text-sm' : 'text-lg'} text-muted-foreground`}>{(bible.has_cover && bible.price_cover > 0) || (bible.has_tab_index && bible.price_tab_index > 0) ? 'Precio base:' : 'Precio:'}</span>
                    <span className={`${(bible.has_cover && bible.price_cover > 0) || (bible.has_tab_index && bible.price_tab_index > 0) ? 'text-sm' : 'text-2xl'} font-bold text-primary`}>
                      ${bible.price?.toFixed(2)}
                    </span>
                    {bible.originalPrice && bible.originalPrice > bible.price && (
                      <span className="text-lg text-muted-foreground line-through">
                        ${bible.originalPrice.toFixed(2)}
                      </span>
                    )}
                  </div>
                  
                  {/* Mostrar precio del forro al lado si solo tiene forro */}
                  {bible.has_cover && bible.price_cover > 0 && !bible.has_tab_index && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-muted-foreground">Forro:</span>
                      <span className="text-sm font-semibold text-green-600">
                        ${bible.price_cover?.toFixed(2)}
                      </span>
                    </div>
                  )}
                  
                  {/* Mostrar precio de pestañas al lado si solo tiene pestañas */}
                  {bible.has_tab_index && bible.price_tab_index > 0 && !bible.has_cover && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-muted-foreground">Pestañas: Con pestañas</span>
                      <span className="text-sm font-semibold text-orange-600">
                        ${bible.price_tab_index?.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
                {/* Total con forro - Solo si tiene forro (sin pestañas) */}
                {bible.has_cover && bible.price_cover > 0 && !bible.has_tab_index && (
                  <div className="flex items-baseline gap-3 pt-2 border-t border-border/50">
                    <span className="text-lg text-muted-foreground">Total con forro:</span>
                    <span className="text-xl sm:text-3xl font-bold text-orange-600">
                      ${bible.total_with_cover?.toFixed(2)}
                    </span>
                  </div>
                )}
                
                {/* Total con pestañas - Solo si tiene pestañas (sin forro) */}
                {bible.has_tab_index && bible.price_tab_index > 0 && !bible.has_cover && (
                  <div className="flex items-baseline gap-3 pt-2 border-t border-border/50">
                    <span className="text-lg text-muted-foreground">Total con pestañas:</span>
                    <span className="text-xl sm:text-3xl font-bold text-purple-600">
                      ${bible.total_with_tab_index?.toFixed(2)}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          <ShareButton
            title={bible.title}
            description={`Biblia versión ${bible.version || bible.authorName || 'Versión no especificada'}`}
            price={bible.price}
            productType="biblia"
          />
        </div>
      </div>


      {/* Bible Characteristics */}
      {bible.characters_bible && Array.isArray(bible.characters_bible) && bible.characters_bible.length > 0 && (
        <div className="pt-4 border-t border-border">
          <h3 className="font-semibold text-foreground mb-3">Características de la Biblia</h3>
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex gap-2 pb-2" style={{ minWidth: 'max-content' }}>
              {bible.characters_bible.map((character, index) => (
                <span
                  key={index}
                  className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm border border-blue-200 whitespace-nowrap flex-shrink-0"
                >
                  #{character}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}


      {/* Features */}
      {bible.features && Array.isArray(bible.features) && bible.features.length > 0 && (
        <div className="pt-4 border-t border-border">
          <h3 className="font-semibold text-foreground mb-3">Características</h3>
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex gap-2 pb-2" style={{ minWidth: 'max-content' }}>
              {bible.features.map((feature, index) => (
                <span
                  key={index}
                  className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm border border-green-200 whitespace-nowrap flex-shrink-0"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BibleInfo;

